import React from 'react'
import type {
  NumberFieldProps as AriaNumberFieldProps,
  GroupProps,
  ValidationResult,
} from 'react-aria-components'
import { NumberField as AriaNumberField, Button, Group } from 'react-aria-components'
import { Description, FieldError, Input, Label, fieldBorderStyles } from './Field'

import { composeTailwindRenderProps, focusRing } from '~/design-system/helpers'
import tv from '~/lib/tv'
import type { VariantProps } from '~/lib/tv'

const numberFieldVariants = tv({
  extend: focusRing,
  base: 'min-h-10 border rounded [text-align:inherit] border-gray-300',
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    ...fieldBorderStyles.variants,
  },
})

export interface NumberFieldProps extends AriaNumberFieldProps {
  label?: string | React.ReactNode
  description?: string
  errorMessage?: string | ((validation: ValidationResult) => string)
  inputVariants?: VariantProps<typeof numberFieldVariants>
  errorProps?: React.HTMLAttributes<HTMLSpanElement>
  decrementButton?: React.ReactNode
  incrementButton?: React.ReactNode
  groupProps?: GroupProps
}

export default function NumberField({
  label,
  description,
  errorMessage,
  inputVariants,
  errorProps = {},
  decrementButton,
  incrementButton,
  groupProps = {},
  ...props
}: NumberFieldProps) {
  return (
    <AriaNumberField
      {...props}
      className={composeTailwindRenderProps(props.className, 'flex flex-col gap-1')}
    >
      {label && <Label>{label}</Label>}
      <Group {...groupProps}>
        {decrementButton ? decrementButton : <Button slot="decrement">-</Button>}
        <Input className={numberFieldVariants(inputVariants)} inputMode="text" pattern="[0-9]*" type="text" />
        {incrementButton ? incrementButton : <Button slot="increment">+</Button>}
      </Group>
      {description && <Description>{description}</Description>}
      <FieldError {...errorProps}>{errorMessage}</FieldError>
    </AriaNumberField>
  )
}
